import { createContext, useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ReactLenis, useLenis } from "lenis/react";
import "lenis/dist/lenis.css";

const EnquiryContext = createContext(null);

const destinationFor = (target) => {
  if (target.id === "top") return 0;
  const inset = Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  return Math.max(0, target.getBoundingClientRect().top + window.scrollY - inset);
};

const AnchorNavigation = () => {
  const lenis = useLenis();
  useEffect(() => {
    if (!lenis) return;
    const navigate = (hash, updateHistory = false) => {
      let target;
      try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch { return; }
      if (!target) return;
      lenis.scrollTo(destinationFor(target), {
        onComplete: () => {
          if (updateHistory && window.location.hash !== hash) window.history.pushState(null, "", hash);
          requestAnimationFrame(() => requestAnimationFrame(() => {
            if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
            target.focus({ preventScroll: true });
          }));
        },
      });
    };
    const onClick = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.hasAttribute("download") || (anchor.target && anchor.target !== "_self")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search || !url.hash) return;
      event.preventDefault();
      navigate(url.hash, true);
    };
    const onHistory = () => navigate(window.location.hash || "#top");
    document.addEventListener("click", onClick);
    window.addEventListener("popstate", onHistory);
    return () => { document.removeEventListener("click", onClick); window.removeEventListener("popstate", onHistory); };
  }, [lenis]);
  return null;
};

export const Experience = ({ children }) => {
  const [interest, setInterest] = useState("");
  const reduced = useReducedMotion();
  return (
    <EnquiryContext.Provider value={{ interest, setInterest }}>
      <ReactLenis root options={{ autoRaf: true, anchors: false, duration: 1.15, smoothWheel: !reduced, syncTouch: false, stopInertiaOnNavigate: true }}>
        <AnchorNavigation />
        {children}
      </ReactLenis>
    </EnquiryContext.Provider>
  );
};

export const useEnquiry = () => {
  const context = useContext(EnquiryContext);
  const lenis = useLenis();
  const reduced = useReducedMotion();
  const enquire = (service) => {
    context.setInterest(service);
    window.setTimeout(() => {
      const target = document.getElementById("enquiry");
      if (!target) return;
      if (lenis) lenis.scrollTo(destinationFor(target), { immediate: !!reduced });
      else target.scrollIntoView({ behavior: reduced ? "instant" : "smooth" });
    }, 150);
  };
  return { ...context, enquire };
};

export const Reveal = ({ children, className = "", delay = 0, ...props }) => {
  const reduced = useReducedMotion();
  return (
    <motion.div className={className} initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }} {...props}>
      {children}
    </motion.div>
  );
};

export const SectionLabel = ({ children, id }) => <h2 className="section-label" data-testid={id}><span aria-hidden="true" />{children}</h2>;